import type { LeaseCompFieldRule } from "@survey-types/survey.core.types";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";

/**
 * Lease Comp Pagination Constants
 *
 * Simplified pagination for lease comparisons.
 * Unlike PropertyComparison, lease comp fields are single-line only,
 * so no complex content splitting is needed.
 */
export const LEASE_COMP_PAGINATION_CONSTANTS = {
  // Height limits (in pixels)
  MAX_HEIGHT_LANDSCAPE_FIRST: 680, // First page with full header (reduced to prevent footer overflow)
  MAX_HEIGHT_LANDSCAPE_SUBSEQUENT: 780, // Subsequent pages
  MAX_HEIGHT_PORTRAIT_FIRST: 1000, // First page with full header
  MAX_HEIGHT_PORTRAIT_SUBSEQUENT: 1000, // Subsequent pages

  // Height per row (in pixels)
  IMAGE_ROW_HEIGHT: 120, // Property images row in header
  FIELD_ROW_HEIGHT: 24, // Each field detail row
  // Reserve one additional row height to avoid footer overlap caused by render variance.
  FIELD_ROW_SAFETY_MARGIN: 24,

  // Buffer space for content overflow
  HEIGHT_BUFFER: 50,
} as const;

/**
 * Page data for a single page in lease comp comparison
 */
export interface LeaseCompPageData {
  leaseComps: LeaseComp[];
  fields: LeaseCompFieldRule[];
  hasImages: boolean;
  isFirstPageOfLeaseCompSet: boolean;
}

/**
 * Page data with row indices for proper striping
 */
export interface LeaseCompPageWithRowIndices {
  pageData: LeaseCompPageData;
  pageIndex: number;
  startingRowIndex: number;
}

/**
 * Get maximum height available for content based on page position and orientation
 * @param hasHeader - Whether this page will have a PDF header
 * @param mode - Page orientation (Landscape or Portrait)
 */
export const getMaxHeight = (
  hasHeader: boolean,
  mode: "Landscape" | "Portrait",
): number => {
  if (mode === "Landscape") {
    return hasHeader
      ? LEASE_COMP_PAGINATION_CONSTANTS.MAX_HEIGHT_LANDSCAPE_FIRST
      : LEASE_COMP_PAGINATION_CONSTANTS.MAX_HEIGHT_LANDSCAPE_SUBSEQUENT;
  }
  return hasHeader
    ? LEASE_COMP_PAGINATION_CONSTANTS.MAX_HEIGHT_PORTRAIT_FIRST
    : LEASE_COMP_PAGINATION_CONSTANTS.MAX_HEIGHT_PORTRAIT_SUBSEQUENT;
};

/**
 * Calculate available height for fields on a page
 * @param hasHeader - Whether page has a header (reduces available space)
 * @param hasImages - Whether page has images row (reduces available space)
 * @param mode - Page orientation
 */
export const calculateAvailableHeight = (
  hasHeader: boolean,
  hasImages: boolean,
  mode: "Landscape" | "Portrait",
): number => {
  let availableHeight = getMaxHeight(hasHeader, mode);

  // Subtract image row height if present
  if (hasImages) {
    availableHeight -= LEASE_COMP_PAGINATION_CONSTANTS.IMAGE_ROW_HEIGHT;
  }

  // Subtract buffer
  availableHeight -= LEASE_COMP_PAGINATION_CONSTANTS.HEIGHT_BUFFER;

  // Keep a safety margin so paginated rows never push footer off page.
  availableHeight -= LEASE_COMP_PAGINATION_CONSTANTS.FIELD_ROW_SAFETY_MARGIN;

  return availableHeight;
};

/**
 * Split lease comps into chunks based on maximum per page
 * @param leaseComps - Array of lease comps to split
 * @param maxPerPage - Maximum lease comps per page
 */
export const chunkLeaseComps = (
  leaseComps: LeaseComp[],
  maxPerPage: number,
): LeaseComp[][] => {
  if (leaseComps.length <= maxPerPage) {
    return [leaseComps];
  }

  const chunks: LeaseComp[][] = [];
  for (let i = 0; i < leaseComps.length; i += maxPerPage) {
    chunks.push(leaseComps.slice(i, i + maxPerPage));
  }
  return chunks;
};

/**
 * Calculate paginated data for lease comparison
 *
 * Simplified algorithm for lease comps:
 * 1. Split lease comps horizontally (max 7 landscape / 4 portrait per page)
 * 2. Split fields vertically based on simple row counting (24px per field)
 * 3. No content splitting needed - all fields are single-line
 *
 * @param leaseComps - Array of lease comps to display
 * @param visibleFields - Array of field rules to display (sorted by order)
 * @param maxLeaseComps - Maximum lease comps per page
 * @param mode - Page orientation (Landscape or Portrait)
 * @param showPropertyPhoto - Whether property photos are shown (affects available height)
 */
export const calculatePaginatedData = (
  leaseComps: LeaseComp[],
  visibleFields: LeaseCompFieldRule[],
  maxLeaseComps: number,
  mode: "Landscape" | "Portrait",
  showPropertyPhoto: boolean = true,
): LeaseCompPageData[] => {
  const pages: LeaseCompPageData[] = [];

  // Handle empty state
  if (leaseComps.length === 0) {
    pages.push({
      leaseComps: [],
      fields: [],
      hasImages: false,
      isFirstPageOfLeaseCompSet: true,
    });
    return pages;
  }

  // Step 1: Split lease comps horizontally
  const leaseCompChunks = chunkLeaseComps(leaseComps, maxLeaseComps);

  // Step 2: For each lease comp chunk, split fields vertically
  leaseCompChunks.forEach((pageLeaseComps) => {
    let remainingFields = [...visibleFields];
    let isFirstPageOfLeaseCompSet = true;

    while (remainingFields.length > 0) {
      // Calculate available height for this page
      const hasHeader = isFirstPageOfLeaseCompSet;
      // Show images on first page of each lease comp set, only when property photos are enabled
      const hasImages = isFirstPageOfLeaseCompSet && showPropertyPhoto;
      const availableHeight = calculateAvailableHeight(
        hasHeader,
        hasImages,
        mode,
      );

      // Calculate how many fields fit on this page
      const fieldsPerPage = Math.max(
        1,
        Math.floor(
          availableHeight / LEASE_COMP_PAGINATION_CONSTANTS.FIELD_ROW_HEIGHT,
        ),
      );

      // Take fields for this page
      const pageFields = remainingFields.slice(0, fieldsPerPage);
      remainingFields = remainingFields.slice(fieldsPerPage);

      // Create page data
      pages.push({
        leaseComps: pageLeaseComps,
        fields: pageFields,
        hasImages,
        isFirstPageOfLeaseCompSet,
      });

      isFirstPageOfLeaseCompSet = false;
    }
  });

  return pages;
};

/**
 * Calculate pages with continuous row indices for proper styling
 *
 * This ensures alternating row backgrounds work correctly across page boundaries
 */
export const calculatePagesWithRowIndices = (
  paginatedData: LeaseCompPageData[],
): LeaseCompPageWithRowIndices[] => {
  let cumulativeRowIndex = 0;

  return paginatedData.map((pageData, pageIndex) => {
    const pageWithRowIndex: LeaseCompPageWithRowIndices = {
      pageData,
      pageIndex,
      startingRowIndex: cumulativeRowIndex,
    };

    // Update cumulative index for next page
    cumulativeRowIndex += pageData.fields.length;

    return pageWithRowIndex;
  });
};
